import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { AuroraBlobs, GrainOverlay, GlassChip, MagneticButton, Reveal, fadeUp, stagger } from '../components/ui';
import { FiPackage } from 'react-icons/fi';

export default function OrdersPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['my-orders'],
    queryFn: () => api.get('/orders/my'),
  });

  const orders = data?.orders || [];

  return (
    <div>
      <section style={{ position: 'relative', background: '#1A100E', overflow: 'hidden', padding: '52px 24px 44px' }}>
        <AuroraBlobs muted />
        <GrainOverlay />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 800, margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <GlassChip>Order history</GlassChip>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="serif" style={{ fontSize: 'clamp(34px, 4vw, 48px)', color: '#FFF', lineHeight: 1.05, letterSpacing: '-0.02em', marginTop: 16 }}>
            My Orders
          </motion.h1>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} style={{ fontSize: 13, color: 'rgba(237,227,209,0.65)', marginTop: 8 }}>
            {orders.length} {orders.length === 1 ? 'order' : 'orders'}
          </motion.div>
        </div>
      </section>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '36px 24px 64px' }}>
        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[...Array(3)].map((_, i) => <div key={i} className="ve-skeleton" style={{ height: 88 }} />)}
          </div>
        ) : orders.length === 0 ? (
          <Reveal style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ display: 'inline-flex', width: 80, height: 80, borderRadius: '50%', background: 'var(--gold-soft)', color: 'var(--gold)', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
              <FiPackage size={36} />
            </div>
            <h2 className="serif" style={{ fontSize: 28, marginBottom: 10 }}>No orders yet</h2>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24 }}>Find your first VritiEthnic piece.</p>
            <MagneticButton to="/" variant="primary">Start shopping</MagneticButton>
          </Reveal>
        ) : (
          <motion.div variants={stagger} initial="hidden" animate="show" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {orders.map(order => (
              <motion.div key={order._id} variants={fadeUp} whileHover={{ y: -3 }}>
                <Link to={`/orders/${order._id}/track`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                  <div style={{
                    background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(229,219,200,0.6)', borderRadius: 16,
                    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                    padding: '22px 26px', display: 'flex', alignItems: 'center', gap: 20,
                  }}>
                    <span style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--gold-soft)', color: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <FiPackage size={18} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, letterSpacing: '0.04em' }}>{order.orderId}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                        {new Date(order.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 4 }}>
                        {order.items?.length} item{order.items?.length > 1 ? 's' : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="serif" style={{ fontSize: 20, fontWeight: 600, marginBottom: 6 }}>₹{order.pricing?.total?.toLocaleString('en-IN')}</div>
                      <StatusPill status={order.status} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    Delivered: { bg: '#E5EFE0', color: '#3F6240' }, Shipped: { bg: '#DDE7F0', color: '#34557A' },
    Confirmed: { bg: '#DDE7F0', color: '#34557A' }, Pending: { bg: '#FBEED6', color: '#8A6020' },
    Returned: { bg: '#F1DBD8', color: '#8B3F36' }, Cancelled: { bg: '#F1DBD8', color: '#8B3F36' },
  };
  const style = map[status] || map.Pending;
  return <span style={{ ...style, padding: '5px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{status}</span>;
}
